import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';

import { InputFieldDef } from '../../../types/admin';
import { formatValue } from './inputFieldDialogHelpers';

export const FieldOverrideDialog: React.FC<{
  field: InputFieldDef;
  value?: Partial<InputFieldDef>;
  onClose: () => void;
  onSave: (patch: Partial<InputFieldDef> | null) => void;
}> = ({ field, value, onClose, onSave }) => {
  const [draft, setDraft] = React.useState<Partial<InputFieldDef>>(value ?? {});
  const [defaultText, setDefaultText] = React.useState(formatValue(value?.default));
  const [enumValues, setEnumValues] = React.useState((value?.enum ?? field.enum ?? []).map(String));

  const save = () => {
    const patch: Partial<InputFieldDef> = {
      label: emptyToUndefined(draft.label),
      required: draft.required,
      hidden: draft.hidden,
      enum: field.type === 'enum' ? enumValues.map((item) => coerceEnum(item)) : undefined,
      min: draft.min,
      max: draft.max,
      maxLength: draft.maxLength,
      order: draft.order,
    };
    if (defaultText !== '-') patch.default = parseMaybeJson(defaultText);
    onSave(compactPatch(patch));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Override {field.key || field.name}</DialogTitle>
      <DialogContent>
        <Box className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField
            label="Label"
            value={draft.label ?? ''}
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          />
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                order: event.target.value === '' ? undefined : Number(event.target.value),
              })
            }
          />
          <TextField
            label="Default"
            value={defaultText === '-' ? '' : defaultText}
            onChange={(event) => setDefaultText(event.target.value)}
          />
          <TextField
            label="Min"
            type="number"
            value={draft.min ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                min: event.target.value === '' ? undefined : Number(event.target.value),
              })
            }
          />
          <TextField
            label="Max"
            type="number"
            value={draft.max ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                max: event.target.value === '' ? undefined : Number(event.target.value),
              })
            }
          />
          <TextField
            label="Max Length"
            type="number"
            value={draft.maxLength ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                maxLength: event.target.value === '' ? undefined : Number(event.target.value),
              })
            }
          />
          {field.type === 'enum' && (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={enumValues}
              onChange={(_event, next) => setEnumValues(next)}
              renderInput={(params) => <TextField {...params} label="Enum" />}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.required === true}
                onChange={(event) => setDraft({ ...draft, required: event.target.checked })}
              />
            }
            label="Required"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.hidden === true}
                onChange={(event) => setDraft({ ...draft, hidden: event.target.checked })}
              />
            }
            label="Hidden"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onSave(null)}>Reset</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>
          Save Override
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function emptyToUndefined(value?: string) {
  return value?.trim() || undefined;
}

function parseMaybeJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function coerceEnum(value: string): string | number {
  const numberValue = Number(value);
  return value.trim() && Number.isFinite(numberValue) ? numberValue : value;
}

function compactPatch(patch: Partial<InputFieldDef>): Partial<InputFieldDef> | null {
  const compact = Object.entries(patch).reduce<Partial<InputFieldDef>>((result, [key, value]) => {
    if (value === undefined || value === '') return result;
    return { ...result, [key]: value };
  }, {});
  return Object.keys(compact).length > 0 ? compact : null;
}
